import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useSchoolRosterOptions } from '../../hooks/useSchoolRosterOptions'
import GroupSelector from './GroupSelector'

export interface RosterFilterValue {
  grade: string | null
  class: string | null
  groupId: string | null
  search: string
}

interface RosterFilterBarProps {
  schoolId: string
  value: RosterFilterValue
  onChange: (value: RosterFilterValue) => void
  searchPlaceholder?: string
  showSearch?: boolean
  // false で学年・クラス・グループを隠す（学校をまたぐ一覧など、学校の選択肢が当てはまらないとき）
  showRosterFilters?: boolean
  // CSV・グループ管理などのボタン
  rightSlot?: ReactNode
}

const SEARCH_DEBOUNCE_MS = 300

const selectClass = (disabled: boolean) =>
  `px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white text-sm ${
    disabled ? 'opacity-50 cursor-not-allowed' : ''
  }`

/**
 * 先生モード共通の絞り込みバー（生徒の状況・宿題一覧・生徒管理）。
 * 学年・クラスは学校の生徒の実データから選ぶ。グループと学年・クラスは同時に使わない
 * （生徒の状況の集計レベルが group / class / grade のいずれか1つに決まるため）。
 */
const RosterFilterBar = ({
  schoolId,
  value,
  onChange,
  searchPlaceholder = '名前・メールアドレス',
  showSearch = true,
  showRosterFilters = true,
  rightSlot,
}: RosterFilterBarProps) => {
  const { grades, classesFor } = useSchoolRosterOptions(schoolId)
  const classes = value.grade ? classesFor(value.grade) : []

  // 検索は入力中の値をローカルに持ち、手が止まってから親に流す
  const [searchInput, setSearchInput] = useState(value.search)
  const valueRef = useRef(value)
  valueRef.current = value

  useEffect(() => {
    setSearchInput(value.search)
  }, [value.search])

  useEffect(() => {
    if (searchInput === valueRef.current.search) return
    const timer = setTimeout(() => {
      onChange({ ...valueRef.current, search: searchInput })
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [searchInput, onChange])

  const groupSelected = !!value.groupId

  return (
    <div className="flex flex-col lg:flex-row gap-3 lg:items-center justify-between">
      {showRosterFilters ? (
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700 whitespace-nowrap">学年</label>
          <select
            value={value.grade ?? 'all'}
            onChange={e => {
              const grade = e.target.value === 'all' ? null : e.target.value
              onChange({ ...value, grade, class: null, groupId: null })
            }}
            disabled={groupSelected}
            className={selectClass(groupSelected)}
          >
            <option value="all">全て</option>
            {grades.map(grade => (
              <option key={grade} value={grade}>{grade}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700 whitespace-nowrap">クラス</label>
          <select
            value={value.class ?? 'all'}
            onChange={e => {
              const cls = e.target.value === 'all' ? null : e.target.value
              onChange({ ...value, class: cls, groupId: null })
            }}
            disabled={!value.grade || groupSelected}
            className={selectClass(!value.grade || groupSelected)}
          >
            <option value="all">全て</option>
            {classes.map(cls => (
              <option key={cls} value={cls}>{cls}</option>
            ))}
          </select>
        </div>

        <GroupSelector
          schoolId={schoolId}
          selectedGroupId={value.groupId}
          onGroupChange={groupId => onChange({ ...value, grade: null, class: null, groupId })}
        />
      </div>
      ) : <div />}

      <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
        {showSearch && (
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">検索</label>
            <input
              type="search"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full sm:w-56 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white text-sm"
            />
          </div>
        )}
        {rightSlot && <div className="flex gap-2">{rightSlot}</div>}
      </div>
    </div>
  )
}

export default RosterFilterBar
